/**
 * PATH: packages/ui/src/kanban/splitCardTitle.ts
 * FILE_ID: UI-KANBAN-LIB-002
 * PURPOSE: Clip a long operator-typed card draft to a title that fits
 *          the BE's Card.title 300-char cap, and report whether
 *          clipping happened so the composer can show a hint.
 * WHY: Bug 2026-05-19 (operator) — "The description is complete
 *      bullshit.. like it should just be title if I created the task
 *      no?". The previous behaviour (added in BUG-2026-05-15) split
 *      overflow into the card description, which surprised operators
 *      who expected manual card creation to be title-only. A long
 *      sentence typed in the composer became a title + an
 *      AI-classify-looking description that the operator never
 *      typed. New behaviour: title-only, always. Newlines collapse
 *      into spaces (so paste is non-destructive), and over-cap input
 *      is clipped at a clean break point with `split: true` so the
 *      composer can flag the truncation.
 * FLOW:
 *   ┌──────────────────┐     ┌───────────────────┐     ┌──────────────────────────┐
 *   │ raw operator txt │ →   │ splitCardTitle()  │ →   │ { title, summary:'', .. }│
 *   └──────────────────┘     └───────────────────┘     └──────────────────────────┘
 *
 *   `summary` is ALWAYS '' from this module. The shape stays
 *   (instead of returning just a string) so callers don't churn —
 *   they keep destructuring `{ title, summary, split }` and only
 *   need to update their hint text.
 *
 * DEPENDENCIES: none (pure I→O, zero external imports)
 * RELATED:
 *   - components/AddCardComposer.tsx: caller — uses `split` to show a
 *     "Title truncated to 300 characters" hint after submit.
 *   - email_kanban.models.Card.title: max_length=300 (the cap we respect)
 */

/** Hard cap on the title field — must match `Card.title.max_length` on the
 *  Django model (`email_kanban/models/card.py`). Re-exported as a constant
 *  so the composer can show a character counter and consumers can guard. */
export const CARD_TITLE_MAX_LEN = 300

/** Soft threshold at which the composer starts showing a character counter.
 *  80% of the hard cap — far enough below that operators have warning
 *  before the BE rejects, close enough that we don't pester them on
 *  every short title. */
export const CARD_TITLE_COUNTER_THRESHOLD = Math.round(CARD_TITLE_MAX_LEN * 0.8)

export interface SplitCardInput {
  /** Raw text from the textarea. */
  raw: string
  /** Optional override for the title cap (defaults to CARD_TITLE_MAX_LEN). */
  maxTitleLen?: number
}

export interface SplitCardOutput {
  /** Clipped, always <= maxTitleLen, newlines collapsed to spaces. */
  title: string
  /** ALWAYS empty string from this module — kept on the type so
   *  existing destructuring callers keep working. `summary` was the
   *  pre-2026-05-19 overflow field; the operator complaint
   *  ("description is complete bullshit") killed that auto-fill. */
  summary: ''
  /** True iff the operator's typed input was clipped (over the cap).
   *  The composer uses this to show a "Title truncated" hint so the
   *  operator knows their tail-end characters didn't land. */
  split: boolean
}

/** Reduce a raw operator draft to a single-line title within the BE's
 *  `Card.title` cap, never producing a description.
 *
 *  Behaviour (rules in priority order):
 *
 *    1. Empty / whitespace-only → `{title:'', summary:'', split:false}`.
 *    2. Newlines collapse into spaces. Operators paste multi-paragraph
 *       text expecting it to become ONE title; the previous behaviour
 *       silently auto-populated a description, which the operator
 *       flagged as "complete bullshit" on 2026-05-19.
 *    3. After collapsing newlines, trimming, and squashing runs of
 *       whitespace, if the input fits → title-only, `split:false`.
 *    4. Over cap → clip at a clean break point (last sentence
 *       terminator, then last space, then hard cut) so we don't chop
 *       a word mid-character. `split:true` so the composer can show
 *       a "title truncated to N chars" hint.
 *
 *  Pure function, no I/O.
 */
export function splitCardTitle({ raw, maxTitleLen = CARD_TITLE_MAX_LEN }: SplitCardInput): SplitCardOutput {
  // Collapse CR/LF → LF, then any sequence of newlines / tabs / spaces
  // into a single space. This turns multi-paragraph paste into a
  // single line that the operator can still see in full before
  // submitting — they're not surprised by a description appearing.
  const flattened = (raw ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/[\n\t]+/g, ' ')
    .replace(/ {2,}/g, ' ')
    .trim()

  if (!flattened) return { title: '', summary: '', split: false }

  if (flattened.length <= maxTitleLen) {
    return { title: flattened, summary: '', split: false }
  }

  // Need to clip. Find the best break point inside the budget.
  const budget = flattened.slice(0, maxTitleLen)
  const breakAt = _pickBreakPoint(budget)
  const title = budget.slice(0, breakAt).trim()
  return { title, summary: '', split: true }
}

function _pickBreakPoint(budget: string): number {
  const len = budget.length
  // Search the last 50 chars for a sentence terminator.
  const windowStart = Math.max(0, len - 50)
  const terminators = ['. ', '! ', '? ']
  let best = -1
  for (const term of terminators) {
    const i = budget.lastIndexOf(term, len - 1)
    if (i >= windowStart && i > best) best = i + term.length
  }
  if (best > 0) return best

  // Fallback: last whitespace anywhere in the budget.
  const lastSpace = budget.lastIndexOf(' ')
  if (lastSpace > 0) return lastSpace

  // Last resort: hard cut.
  return len
}
